<template>
  <page-wrapper
    error-key="quotaOrder"
    :breadcrumbs="breadcrumbs"
    :sticky-title="!!$vuetify.display.mdAndUp"
    class="book"
  >
    <template #title>
      <div class="book__header">
        <div>
          <div class="h3">
            Бронирование №
            <skeleton-loader v-if="!book" width="120px" type="h3" inline />
            <template v-else>
              {{ book.quotaOrder.orderInfo.number }}/{{ book.quotaOrder.orderInfo.subNumber }}
            </template>
          </div>
          <skeleton-loader v-if="!book" width="100px" height="17px" class="mt-1" />
          <book-status v-else :status="book.quotaOrder.orderInfo.status" />
        </div>
        <book-actions v-if="!$vuetify.display.smAndDown" />
      </div>
    </template>

    <template v-if="book && book.quotaOrder.orderInfo.status === OrderStatus.NotConfirmed">
      <e-alert v-if="book.quotaOrder.accommodation.accommodationHasBegun" variant="error" class="mb-6">
        <div class="paragraph-m_h font-600">Дата и время заезда наступили</div>
        <b class="paragraph-s_h text-secondary font-400">
          Подтверждение и аннулирование заказа возможно только через
          <router-link :to="{ name: RouteNames.Support }" class="link">Поддержку</router-link>
        </b>
      </e-alert>
      <e-alert v-else-if="book.quotaOrder.orderInfo.hasNonStandard" class="mb-6">
        <div class="paragraph-m_h font-600">Заказ проживания нестандартный</div>
        <b class="paragraph-s_h text-secondary font-400">
          Данный тип заказов можно подтвердить или аннулировать только через
          <router-link :to="{ name: RouteNames.Support }" class="link">Поддержку</router-link>
        </b>
      </e-alert>
      <e-alert v-else class="mb-6">
        <b class="paragraph-m">
          Внимание. Стоимость проживания и услуг может отличаться от текущих, после подтверждения бронирования
        </b>
      </e-alert>
    </template>

    <div class="book__common font-600">
      <property-block content-max-width="100%" breakpoint="md" class="w-100">
        <template #text> Общая информация </template>
        <template #content>
          <div class="book__common-items">
            <property-item title="Аккаунт" class="book__common-item">
              <skeleton-loader v-if="!book" width="100px" type="paragraph-m_h" />
              <template v-else>
                {{ book.quotaOrder.commonInfo.contractNumber }}
              </template>
            </property-item>
            <property-item title="Тарифный план" class="book__common-item">
              <skeleton-loader v-if="!book" width="100px" type="paragraph-m_h" />
              <template v-else>
                {{ book.quotaOrder.commonInfo.quotaShortName }}
              </template>
            </property-item>
            <property-item title="Категория номера" class="book__common-item">
              <skeleton-loader v-if="!book" width="100px" type="paragraph-m_h" />
              <template v-else>
                {{ book.quotaOrder.commonInfo.roomCategoryName }}
              </template>
            </property-item>
            <property-item title="Дата заказа" class="book__common-item">
              <skeleton-loader v-if="!book" width="100px" type="paragraph-m_h" />
              <template v-else>
                {{ stringToLocaleDate(book.quotaOrder.commonInfo.orderDate) }}
              </template>
            </property-item>
          </div>
        </template>
      </property-block>

      <property-block content-max-width="100%" breakpoint="md" class="w-100">
        <template #text> Данные проживания </template>
        <template #content>
          <div class="book__common-items">
            <property-item title="Период проживания" class="book__common-item">
              <skeleton-loader v-if="!book" width="100px" type="paragraph-m_h" />
              <template v-else>
                {{ stringToLocaleDate(book.quotaOrder.accommodation.period.from) }} -
                {{ stringToLocaleDate(book.quotaOrder.accommodation.period.to) }}
              </template>
            </property-item>
            <div class="d-flex book__common-item">
              <property-item title="Суток" class="w-50">
                <skeleton-loader v-if="!book" width="30px" type="paragraph-m_h" />
                <template v-else>
                  {{ book.quotaOrder.accommodation.daysCount }}
                </template>
              </property-item>
              <property-item title="Номеров" class="w-50">
                <skeleton-loader v-if="!book" width="30px" type="paragraph-m_h" />
                <template v-else>
                  {{ book.quotaOrder.accommodation.roomCount }}
                </template>
              </property-item>
            </div>
            <property-item title="Стоимость проживания" class="book__common-item">
              <skeleton-loader v-if="!book" width="100px" type="paragraph-m_h" />
              <template v-else>
                {{ formatPrice(book.quotaOrder.accommodation.cost) }}
              </template>
            </property-item>
            <property-item title="Валюта" class="book__common-item">
              <skeleton-loader v-if="!book" width="100px" type="paragraph-m_h" />
              <template v-else>
                {{ getCurrencyText(book.quotaOrder.accommodation.currencyISOCode) }}
              </template>
            </property-item>
          </div>
        </template>
      </property-block>
    </div>

    <div class="d-flex flex-wrap col-gap-20 mt-6">
      <tabs-list v-if="book" :tabs="tabs">
        <template v-for="(room, index) in book?.quotaOrder.rooms" :key="index" v-slot:[`tab.${index}`]>
          <div class="ga-sm-4">
            <div class="paragraph-l mb-4 font-600">Данные о проживании и туристах</div>
            <div class="d-flex">
              <div class="mr-4 paragraph-l">
                Вариант размещения:&nbsp;
                <span class="font-600">
                  <span v-if="room.nonStandard">[Н]</span>
                  {{ room.roomPlacingName }}
                </span>
              </div>
              <status-icon :status="room.status" size="20" />
            </div>
            <div class="mt-2">
              <e-data-table v-if="!$vuetify.display.smAndDown" :headers="placesHeaders" :items="room.places">
                <template v-slot:[`item.packetName`]="{ item }">
                  <div v-for="({ packetName }, index) in item.packets" :key="index">
                    {{ packetName }}
                  </div>
                </template>
                <template v-slot:[`item.placeType`]="{ item }">
                  <div v-for="(_, index) in item.packets" :key="index">
                    {{ PLACE_TYPES[item.placeType] }}
                  </div>
                </template>
                <template v-slot:[`item.from`]="{ item }">
                  <div v-for="({ period }, index) in item.packets" :key="index">
                    {{ stringToLocaleDate(period.from) }}
                  </div>
                </template>
                <template v-slot:[`item.to`]="{ item }">
                  <div v-for="({ period }, index) in item.packets" :key="index">
                    {{ stringToLocaleDate(period.to) }}
                  </div>
                </template>
                <template v-slot:[`item.cost`]="{ item }">
                  <div v-for="({ cost }, index) in item.packets" :key="index" class="text-end">
                    <span class="text-black">{{ formatPrice(cost) }}</span>
                  </div>
                </template>
                <template v-slot:[`item.status`]="{ item }">
                  <div v-for="(packet, index) in item.packets" :key="index">
                    <status-icon :status="packet.status" size="20" />
                  </div>
                </template>
              </e-data-table>

              <div v-else>
                <div class="book-room__place" v-for="place in room.places" :key="place.touristName">
                  <div class="book-room__place-row mb-1">
                    <b>{{ place.touristName }}</b>
                    <span class="paragraph-s font-weight-regular">{{ place.touristType }}</span>
                  </div>

                  <template v-for="packet in place.packets" :key="packet.packetName">
                    <div class="book-room__place-row">
                      <div class="paragraph-s text-secondary">
                        <span class="book-room__place-badge">{{ packet.packetName }}</span>
                        {{ PLACE_TYPES[place.placeType] }}
                      </div>
                      <status-icon :status="packet.status" size="20" />
                    </div>
                    <div class="book-room__place-row">
                      <div class="d-flex paragraph-s text-secondary align-center ga-1">
                        <e-icon name="calendarTick" size="20" />
                        {{ stringToLocaleDate(packet.period.from) }} - {{ stringToLocaleDate(packet.period.to) }}
                      </div>
                      <b>{{ formatPrice(packet.cost) }}</b>
                    </div>
                  </template>
                </div>
              </div>
              <div class="book__total">
                Стоимость проживания:&nbsp;
                <b>{{ formatPrice(room.summaryAccommodationCost) }}</b>
              </div>
            </div>
            <template v-if="room.services?.length">
              <b class="paragraph-l">Услуги бронирования</b>
              <e-data-table v-if="!$vuetify.display.smAndDown" :headers="servicesHeaders" :items="room.services">
                <template v-slot:[`item.period.from`]="{ value }">
                  {{ stringToLocaleDate(value) }}
                </template>
                <template v-slot:[`item.period.to`]="{ value }">
                  {{ stringToLocaleDate(value) }}
                </template>
                <template v-slot:[`item.cost`]="{ value }">
                  <div class="text-end">
                    <span class="text-black">{{ value }}</span>
                  </div>
                </template>
                <template v-slot:[`item.status`]="{ value }">
                  <div>
                    <status-icon :status="value" size="20" />
                  </div>
                </template>
              </e-data-table>
              <div v-else>
                <div v-for="service in room.services" :key="service.serviceName" class="book-room__place">
                  <div class="book-room__place-row mb-1">
                    <b>{{ service.serviceName }}</b>
                    <span class="paragraph-l font-weight-regular">{{ service.amount }}</span>
                  </div>

                  <div class="book-room__place-row">
                    <div class="paragraph-s text-secondary">
                      {{ service.touristName }}
                    </div>
                    <status-icon :status="service.status" size="20" />
                  </div>
                  <div class="book-room__place-row">
                    <div class="d-flex paragraph-s text-secondary align-center ga-1">
                      <e-icon name="calendarTick" />
                      {{ stringToLocaleDate(service.period.from) }} - {{ stringToLocaleDate(service.period.to) }}
                    </div>
                    <span>{{ service.cost }}</span>
                  </div>
                </div>
              </div>

              <div class="book__total">
                Стоимость услуг:&nbsp;<b>{{ room.summaryServiceCost }}</b>
              </div>
            </template>
          </div>
        </template>
      </tabs-list>

      <tabs-list v-if="!book" :tabs="[]" loading>
        <template v-for="i in 2" :key="i" v-slot:[`tab.${i-1}`]>
          <div class="ga-sm-4">
            <div class="paragraph-l mb-4 font-600">Данные о проживании и туристах</div>
            <div class="mr-4 paragraph-l">
              Вариант размещения:&nbsp;
              <skeleton-loader width="100px" height="22px" inline />
            </div>

            <skeleton-loader width="100%" height="100%" />
          </div>
          <e-data-table
            v-if="!$vuetify.display.smAndDown"
            :headers="servicesHeaders"
            :items="[]"
            :loading-rows="4"
            loading
          />
          <div v-else class="mt-2">
            <skeleton-loader v-for="i in 4" :key="i" width="100%" height="100px" class="mb-2" />
          </div>
          <div class="book__total">
            Стоимость услуг:&nbsp;
            <skeleton-loader width="80px" height="19px" inline />
          </div>
        </template>
      </tabs-list>

      <divider class="my-6" />

      <div class="d-flex flex-column ga-4 flex-lg-row w-100">
        <div v-if="book && book.quotaOrder.accommodation.specConditions" class="book__addition">
          <e-icon class="text-primary mr-3" size="18" name="alert" />
          <div>
            <b class="paragraph-m">Особые условия</b>
            <div class="paragraph-s text-secondary">{{ book.quotaOrder.accommodation.specConditions }}</div>
          </div>
        </div>
        <skeleton-loader v-if="!book" width="100%" height="62px" />
        <div v-if="book && book.quotaOrder.accommodation.orderWishes" class="book__addition">
          <e-icon class="text-primary mr-3" size="18" name="alert" />
          <div>
            <b class="paragraph-m">Пожелания</b>
            <div class="paragraph-s text-secondary">
              {{ book.quotaOrder.accommodation.orderWishes }}
            </div>
          </div>
        </div>
        <skeleton-loader v-if="!book" width="100%" height="62px" />
      </div>
    </div>

    <div v-if="$vuetify.display.smAndDown" class="book__floating-bar">
      <book-actions />
    </div>
  </page-wrapper>
</template>

<script setup lang="ts">
import { computed, toRef, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useDisplay } from 'vuetify';
import type { TabsListTab } from '@/core/types/components/components';
import type { BreadcrumbsItem } from '@/core/types/components/helps';
import type { DataTableHeaders } from '@/core/types/components/table';
import Divider from '@/core/components/helpers/Divider.vue';
import PageWrapper from '@/core/components/interface/PageWrapper.vue';
import PropertyBlock from '@/core/components/interface/PropertyBlock.vue';
import PropertyItem from '@/core/components/interface/PropertyItem.vue';
import SkeletonLoader from '@/core/components/interface/SkeletonLoader.vue';
import TabsList from '@/core/components/interface/Tabs.vue';
import { EAlert, EDataTable, EIcon } from '@/core/components/ui';
import { RouteNames } from '@/core/plugins/router/routeNames';
import { getCurrencyText } from '@/core/utils/currency';
import { stringToLocaleDate } from '@/core/utils/date';
import { formatPrice } from '@/core/utils/number';
import { OrderStatus } from '@/graphql/generated';
import BookActions from '@/parts/books/components/BookActions.vue';
import BookStatus from '@/parts/books/components/BookStatus.vue';
import StatusIcon from '@/parts/books/components/StatusIcon.vue';
import { useBooksStore } from '@/parts/books/store/books';
import { PLACE_TYPES } from '@/parts/books/utils/consts';

const display = useDisplay();
const route = useRoute();
const { getBook, book } = useBooksStore();

const id = toRef(() => +route.params.b_id);

watch(
  id,
  async () => {
    if (!id.value) {
      return;
    }

    await getBook({ orderId: id.value });
  },
  {
    immediate: true,
  },
);

const tabs = toRef<() => TabsListTab[]>(
  () =>
    book.value?.quotaOrder.rooms.map((_, index) => ({
      title: `Номер ${index + 1}`,
      key: `${index}`,
    })) || [],
);

const placesHeaders = computed<DataTableHeaders>(() => [
  {
    title: 'Турист',
    align: 'start',
    key: 'touristName',
  },
  { title: 'Тип туриста', key: 'touristType', align: 'start', width: display.mdAndDown.value ? 120 : 160 },
  { title: 'Место', key: 'placeType', align: 'start', width: display.mdAndDown.value ? 100 : 165 },
  {
    title: 'Группа услуг',
    key: 'packetName',
    align: 'start',
    width: display.mdAndDown.value ? 116 : 160,
  },
  { title: 'Дата заезда', key: 'from', align: 'start', width: display.mdAndDown.value ? 116 : 160 },
  { title: 'Дата выезда', key: 'to', align: 'start', width: display.mdAndDown.value ? 116 : 160 },
  { title: 'Стоимость', key: 'cost', align: 'end', width: display.mdAndDown.value ? 110 : 160 },
  { title: '', key: 'status', sortable: false, align: 'end', width: display.mdAndDown.value ? 40 : 50 },
]);

const servicesHeaders: DataTableHeaders = [
  {
    title: 'Наименование услуги',
    key: 'serviceName',
    align: 'start',
  },
  {
    title: 'Турист',
    key: 'touristName',
    align: 'start',
    width: 160,
  },
  {
    title: 'Количество, ед изм.',
    key: 'amount',
    align: 'start',
    width: 190,
  },
  {
    title: 'Дата с',
    key: 'period.from',
    align: 'start',
    width: 120,
  },
  {
    title: 'Дата по',
    key: 'period.to',
    align: 'start',
    width: 120,
  },
  {
    title: 'Стоимость',
    key: 'cost',
    align: 'end',
    width: 126,
  },
  { title: '', key: 'status', sortable: false, align: 'end', width: 50 },
];

const breadcrumbs = computed<BreadcrumbsItem[]>(() => [
  {
    icon: 'keyBook',
    title: 'Бронирования',
    to: {
      name: RouteNames.Books,
    },
  },
  {
    title: book.value
      ? `Бронирование №${book.value.quotaOrder.orderInfo.number}/${book.value.quotaOrder.orderInfo.subNumber}`
      : '',
    loading: !book.value,
  },
]);
</script>

<style scoped lang="scss">
.book {
  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;

    width: 100%;
  }

  &__common {
    max-width: 1360px;

    @include lg-and-down {
      justify-content: flex-start;
    }

    &-items {
      display: flex;
      flex-wrap: wrap;
      justify-content: space-between;

      column-gap: 40px;

      @include lg-and-down {
        justify-content: flex-start;
      }
    }

    &-item {
      min-width: 240px;
      margin-bottom: 24px;

      &:last-of-type {
        min-width: 120px;
      }

      @include lg-and-down {
        min-width: unset;
      }

      @include xs {
        margin-bottom: 16px;

        column-gap: 40px;
      }
    }
  }

  &__total {
    margin-top: 16px;
    margin-right: 56px;
    margin-bottom: 8px;
    padding-right: 12px;

    text-align: right;

    @include md-and-down {
      padding-right: 2px;
    }

    @include xs {
      margin-top: 8px;
      margin-right: 10px;
    }
  }

  &-room__place {
    padding: 16px 12px;

    font-weight: 600;

    border-radius: $border-radius;

    &-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    &-badge {
      margin-right: 8px;
      padding: 2px;

      background-color: $primary-tonal-color;
      border-radius: 4px;
    }

    &:nth-last-of-type(2n + 2) {
      background-color: $bg-light-color;
    }
  }

  &__addition {
    display: flex;

    width: 100%;
    padding: 12px;

    line-height: 1;

    border: 1px solid $border-color-light;
    border-radius: $border-radius;
  }

  &__floating-bar {
    position: sticky;
    bottom: 0;

    margin-top: 24px;
    padding: 12px 0;

    background-color: $main-bg-color;
  }
}
</style>
